import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { Token } from '@frontend/authentication-v2';
import { Locale } from '@frontend/common';
import { CreateDocument, CreateDocumentFile, Document, DocumentListResponse, DocumentQueryParams, UpdateDocument } from '@frontend/document/types';

export class DocumentClient extends APIClient {
    public static readonly endpoint = '/document-api/v1';

    public static async fetchDocuments(queryParams?: ApiQueryParams<DocumentQueryParams>): Promise<DocumentListResponse> {
        return await this.apiPaginated<DocumentListResponse, DocumentQueryParams>(`${this.endpoint}/documents`, queryParams);
    }

    public static async resolveDocuments(ids: string[], field: keyof Document = 'id'): Promise<DocumentListResponse> {
        const response = await this.post(`${this.endpoint}/documents-resolve`, { field, ids: ids });
        return await this.handleResponse<DocumentListResponse>(response);
    }

    public static async deleteDocuments(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/documents-delete`, { ids: ids });
        return await this.handleVoidResponse(response);
    }

    public static async fetchAccountDocuments(accountId: string, queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<DocumentListResponse> {
        return await this.apiPaginated<DocumentListResponse, DefaultQueryParams>(`${this.endpoint}/accounts/${accountId}/documents`, queryParams);
    }

    public static async postDocument(accountId: string, body: CreateDocument): Promise<Document> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/documents`, body);
        return await this.handleResponse<Document>(response);
    }

    public static async fetchAccountDocument(accountId: string, documentId: string): Promise<Document> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/documents/${documentId}`);
        return await this.handleResponse<Document>(response);
    }

    public static async patchDocument(accountId: string, documentId: string, body: UpdateDocument): Promise<Document> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/documents/${documentId}`, body);
        return await this.handleResponse<Document>(response);
    }

    public static async deleteDocument(accountId: string, documentId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/documents/${documentId}`);
        return await this.handleVoidResponse(response);
    }

    // Document file
    public static async postDocumentFile(accountId: string, document_id: string, body: CreateDocumentFile): Promise<Document> {
        const formData = new FormData();
        formData.append('file', body.file!);
        if (body.language) formData.append('language', body.language);
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/documents/${document_id}/file`, formData);
        return await this.handleResponse<Document>(response);
    }

    public static async fetchDocumentFile(accountId: string, document_id: string, language?: Locale, withCredentials?: Token): Promise<Blob> {
        const response = await this.fetch(
            `${this.endpoint}/accounts/${accountId}/documents/${document_id}/file${language ? `?language=${language}` : ''}`,
            {},
            true,
            withCredentials
        );
        return await this.handleBlobResponse(response);
    }

    public static async fetchDocumentSasUrl(accountId: string, document_id: string, language?: Locale): Promise<string> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/documents/${document_id}/file-sas${language ? `?language=${language}` : ''}`);
        // The endpoint returns a 307 redirect to Azure SAS URL - response.url contains the final URL after redirect
        return response.url;
    }

    public static async generateDocumentSasUrl(accountId: string, document_id: string, language?: Locale): Promise<string> {
        const response = await this.fetch(
            `${this.endpoint}/accounts/${accountId}/documents/${document_id}/generate-file-sas-url${language ? `?language=${language}` : ''}`
        );
        return await this.handleResponse<string>(response);
    }

    //Sync
    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
